/**
 * Настройки (§78): профиль, пароль, история согласий, удаление данных (§49).
 */
import { useState, type FormEvent } from 'react';
import { LanguageSwitcher } from '../components/layout/LanguageSwitcher';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deletionScopeLabels, type DeletionScope } from '@alterium/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusDot } from '../components/ui/StatusDot';
import { accountApi } from '../api/account';
import { authApi } from '../api/auth';
import { useAuthStore } from '../stores/auth';
import { formatDate } from '../i18n/format';
import { useTranslation } from 'react-i18next';
import { errorText } from '../i18n/errors';

/** Тип согласия приходит с сервера: держим ключ, переводим при отрисовке. */
const consentKey = (type: string): string => `enum.consent.${type}`;

/** §49: удалять можно по частям, а не только аккаунт целиком. */
const partialScopes: DeletionScope[] = ['avatar', 'voice', 'photos', 'source_videos', 'persona'];

export function SettingsPage() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const setSession = useAuthStore((state) => state.setSession);
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();

  // Экран рендерится только под RequireAuth, поэтому user уже заполнен
  // и зеркалить его в эффекте не нужно.
  const [name, setName] = useState(user?.name ?? '');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const consents = useQuery({ queryKey: ['consents'], queryFn: authApi.consents });
  const deletions = useQuery({
    queryKey: ['deletion-requests'],
    queryFn: accountApi.deletionRequests,
  });

  const updateProfile = useMutation({
    mutationFn: () => accountApi.update({ name: name.trim() || null }),
    onSuccess: (updated) => setSession(updated),
  });

  const changePassword = useMutation({
    mutationFn: () => accountApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      // Смена пароля гасит все сессии, включая текущую.
      clear();
    },
  });

  const requestDeletion = useMutation({
    mutationFn: (scope: DeletionScope) =>
      accountApi.requestDeletion(scope, scope === 'account' ? deletePassword : undefined),
    onSuccess: (_result, scope) => {
      if (scope === 'account') clear();
      else void queryClient.invalidateQueries({ queryKey: ['deletion-requests'] });
    },
  });

  return (
    <div className="flex max-w-[720px] flex-col gap-8">
      <PageHeader title={t('nav.settings')} />

      <Card>
          <CardBody className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-card-title">{t('settings.language')}</h2>
              <p className="text-meta text-content-secondary">{t('settings.languageHint')}</p>
            </div>
            <LanguageSwitcher />
          </CardBody>
        </Card>

        <Card>
        <CardBody className="flex flex-col gap-5">
          <h2 className="text-card-title">{t('settings.profile')}</h2>
          <Input label={t('auth.field.email')} value={user?.email ?? ''} disabled readOnly />
          <Input label={t('auth.field.name')} value={name} onChange={(event) => setName(event.target.value)} />
          {updateProfile.isError ? (
            <Alert tone="error">{errorText(updateProfile.error)}</Alert>
          ) : null}
          {updateProfile.isSuccess ? <Alert tone="success">{t('common.saved')}</Alert> : null}
          <div>
            <Button onClick={() => updateProfile.mutate()} disabled={updateProfile.isPending}>{t('common.save')}</Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-5">
          <h2 className="text-card-title">{t('auth.field.password')}</h2>
          <p className="text-meta text-content-muted">{t('settings.passwordWarning')}</p>
          {changePassword.isError ? (
            <Alert tone="error">{errorText(changePassword.error)}</Alert>
          ) : null}
          <form
            className="flex flex-col gap-4"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              changePassword.mutate();
            }}
          >
            <Input
              label={t('settings.currentPassword')}
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
            <Input
              label={t('auth.reset.title')}
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <div>
              <Button type="submit" disabled={changePassword.isPending}>{t('settings.changePassword')}</Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-4">
          <h2 className="text-card-title">{t('settings.consents')}</h2>
          <p className="text-meta text-content-muted">{t('settings.consentsNote')}</p>
          <div className="flex flex-col gap-2">
            {(consents.data ?? []).map((record) => (
              <div
                key={`${record.type}-${record.version}-${record.acceptedAt}`}
                className="flex items-center gap-3 text-body"
              >
                <StatusDot tone={record.isCurrent ? 'ok' : 'idle'} />
                <span className="text-content">
                  {t(consentKey(record.type), { defaultValue: record.type })}
                </span>
                <span className="text-content-muted">{t('settings.version', { value: record.version })}</span>
                <span className="ml-auto text-meta text-content-muted">
                  {formatDate(record.acceptedAt)}
                </span>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="flex flex-col gap-5">
          <h2 className="text-card-title">{t('settings.deletion')}</h2>
          <p className="text-body text-content-secondary">{t('settings.deletionNote')}</p>

          <div className="flex flex-wrap gap-2">
            {partialScopes.map((scope) => (
              <Button
                key={scope}
                size="sm"
                onClick={() => requestDeletion.mutate(scope)}
                disabled={requestDeletion.isPending}
              >
                {deletionScopeLabels[scope]}
              </Button>
            ))}
          </div>

          {(deletions.data ?? []).length > 0 ? (
            <div className="flex flex-col gap-2 border-t border-line pt-4">
              {(deletions.data ?? []).map((request) => (
                <div key={request.id} className="flex items-center gap-3 text-meta">
                  <StatusDot
                    tone={
                      request.status === 'completed'
                        ? 'ok'
                        : request.status === 'failed'
                          ? 'fail'
                          : 'warn'
                    }
                  />
                  <span className="text-content">{deletionScopeLabels[request.scope]}</span>
                  <span className="text-content-muted">{request.status}</span>
                </div>
              ))}
            </div>
          ) : null}

          <div className="border-t border-line pt-5">
            {requestDeletion.isError ? (
              <Alert tone="error">{errorText(requestDeletion.error)}</Alert>
            ) : null}

            {confirmingDelete ? (
              <div className="mt-3 flex flex-col gap-4">
                <Input
                  label={t('settings.confirmPassword')}
                  type="password"
                  autoComplete="current-password"
                  value={deletePassword}
                  onChange={(event) => setDeletePassword(event.target.value)}
                />
                <div className="flex gap-3">
                  <Button
                    className="border-error/50 text-error hover:border-error"
                    onClick={() => requestDeletion.mutate('account')}
                    disabled={requestDeletion.isPending || deletePassword.length === 0}
                  >{t('settings.deleteForever')}</Button>
                  <Button variant="tertiary" onClick={() => setConfirmingDelete(false)}>{t('common.cancel')}</Button>
                </div>
              </div>
            ) : (
              <Button
                className="mt-1 border-error/50 text-error hover:border-error"
                onClick={() => setConfirmingDelete(true)}
              >{t('settings.deleteAccount')}</Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
